package app.footyguide.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import kotlinx.datetime.*
import kotlinx.browser.window

/**
 * Custom themed calendar modal for date selection.
 * Matches the "Night Stadium" aesthetic.
 */
@Composable
fun CustomCalendarModal(
    initialDate: LocalDate,
    today: LocalDate,
    onDateSelected: (LocalDate) -> Unit,
    onClose: () -> Unit
) {
    val strings = app.footyguide.core.locale.LocalStrings.current
    var currentMonth by remember { mutableStateOf(LocalDate(initialDate.year, initialDate.month, 1)) }
    
    DisposableEffect(Unit) {
        val bodyStyle = kotlinx.browser.document.body?.style
        val previousOverflow = bodyStyle?.getPropertyValue("overflow") ?: ""
        val previousTouchAction = bodyStyle?.getPropertyValue("touch-action") ?: ""
        bodyStyle?.setProperty("overflow", "hidden")
        bodyStyle?.setProperty("touch-action", "none")
        onDispose {
            if (previousOverflow.isNotBlank()) {
                bodyStyle?.setProperty("overflow", previousOverflow)
            } else {
                bodyStyle?.removeProperty("overflow")
            }
            if (previousTouchAction.isNotBlank()) {
                bodyStyle?.setProperty("touch-action", previousTouchAction)
            } else {
                bodyStyle?.removeProperty("touch-action")
            }
        }
    }

    Div({
        classes("fg-modal-backdrop-animated")
        style {
            position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.85)")); property("backdrop-filter", "blur(16.px)")
            display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); alignItems(AlignItems.Center)
            property("z-index", "3000")
            property("touch-action", "none")
            property("overscroll-behavior", "contain")
        }
        onClick { onClose() }
    }) {
        Div({
            classes("fg-sheet-animated")
            onClick { it.stopPropagation() }
            style {
                width(90.percent)
                maxWidth(360.px)
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                borderRadius(24.px)
                padding(22.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("box-shadow", "0 20px 40px rgba(22, 24, 29, 0.15)")
            }
        }) {
            // CALENDAR HEADER
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(18.px)
                }
            }) {
                Button({
                    onClick { currentMonth = currentMonth.minus(DatePeriod(months = 1)) }
                    style {
                        background("none")
                        border(0.px)
                        color(Color("#16181D"))
                        cursor("pointer")
                        padding(4.px)
                    }
                }) { LucideIcon(Icons.ARROW_LEFT, size = 20, color = "#16181D") }
                
                Span({
                    classes("fg-calendar-month-title")
                    style { 
                        fontWeight(800)
                        fontSize(15.px)
                        fontFamily("Sora", "Plus Jakarta Sans", "sans-serif")
                        color(Color("#16181D"))
                        property("text-transform", "uppercase")
                        letterSpacing((-0.2).px)
                    }
                }) {
                    val monthName = formatMonthLocalized(currentMonth.month.number, strings.localeTag)
                    Text(monthName.uppercase() + " ${currentMonth.year}")
                }
                
                Button({
                    onClick { currentMonth = currentMonth.plus(DatePeriod(months = 1)) }
                    style {
                        background("none")
                        border(0.px)
                        color(Color("#16181D"))
                        cursor("pointer")
                        padding(4.px)
                    }
                }) { LucideIcon(Icons.CHEVRON_RIGHT, size = 20, color = "#16181D") }
            }

            // DAYS OF WEEK
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(7, 1fr)")
                    property("text-align", "center")
                    marginBottom(10.px)
                }
            }) {
                strings.calendarWeekdaysHeader.forEach {
                    Span({
                        style {
                            fontSize(11.px)
                            fontWeight(700)
                            fontFamily("Inter, sans-serif")
                            color(Color("#71717A"))
                        }
                    }) { Text(it) }
                }
            }

            // DAYS GRID
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "repeat(7, 1fr)")
                    gap(6.px)
                }
            }) {
                val daysInMonth = getDaysInMonth(currentMonth.year, currentMonth.month)
                val firstDayOfWeek = currentMonth.dayOfWeek.isoDayNumber % 7 // 0 is Sunday
                
                // Spacers for first week
                repeat(firstDayOfWeek) { Div {} }
                
                for (day in 1..daysInMonth) {
                    val date = LocalDate(currentMonth.year, currentMonth.month, day)
                    val isSelected = date == initialDate
                    val isToday = date == today
                    
                    Div({
                        if (isSelected) classes("fg-calendar-day-selected")
                        onClick { 
                            onDateSelected(date)
                            onClose()
                        }
                        style {
                            padding(8.px, 0.px)
                            borderRadius(10.px)
                            cursor("pointer")
                            property("text-align", "center")
                            fontSize(13.px)
                            fontWeight(700)
                            fontFamily("Inter, sans-serif")
                            position(Position.Relative)
                            property("transition", "all 0.15s ease")
                            
                            if (isSelected) {
                                property("background-color", "var(--accent-fill, #16181D) !important")
                                property("color", "var(--accent-on-fill, #FFFFFF) !important")
                                property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.18)")
                            } else {
                                property("color", "#16181D !important")
                                if (isToday) {
                                    border(1.5.px, LineStyle.Solid, Color("var(--accent-fill, #16181D)"))
                                }
                            }
                        }
                    }) {
                        Span({
                            style {
                                property("color", if (isSelected) "var(--accent-on-fill, #FFFFFF) !important" else "#16181D !important")
                                fontWeight(800)
                            }
                        }) {
                            Text(day.toString())
                        }
                        
                        if (isToday && isSelected) {
                            Div({
                                style {
                                    position(Position.Absolute)
                                    bottom(3.px)
                                    left(50.percent)
                                    width(4.px)
                                    height(4.px)
                                    borderRadius(50.percent)
                                    property("background-color", "var(--accent-on-fill, #FFFFFF) !important")
                                    property("transform", "translateX(-50%)")
                                }
                            })
                        }
                    }
                }
            }
            
            Button({
                onClick { onClose() }
                style {
                    marginTop(20.px)
                    padding(11.px)
                    borderRadius(12.px)
                    border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                    backgroundColor(Color("#FAFAF7"))
                    color(Color("#16181D"))
                    fontFamily("Sora, 'Plus Jakarta Sans', sans-serif")
                    fontSize(13.px)
                    fontWeight(800)
                    cursor("pointer")
                    property("transition", "all 0.15s ease")
                }
            }) { Text(strings.close.uppercase()) }
        }
    }
}

private fun getDaysInMonth(year: Int, month: Month): Int = when (month) {
    Month.FEBRUARY -> if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 29 else 28
    Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
    else -> 31
}

private fun formatMonthLocalized(monthNumber: Int, localeTag: String): String {
    val d = js("new Date(2000, 0, 1)")
    d.setMonth(monthNumber - 1)
    return d.toLocaleDateString(localeTag, js("{ month: 'long' }")) as String
}
